/**
 * useAdvancedFilters Hook
 *
 * Manages advanced filter state and converts to API filter format.
 * Provides drawer state management and filter construction.
 * Supports metadata-driven default filters.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import type { Filter, ConstraintType } from '@/types/api.types'
import type { DefaultFilter } from '@/services/metadataService'
import type { FilterRow } from './advancedFiltersTypes'
import {
  operatorRequiresValue,
  operatorRequiresMultipleValues,
  operatorRequiresRange,
} from './operatorConfig'

export interface UseAdvancedFiltersOptions {
  /** Default filters from metadata (applied on initial load) */
  defaultFilters?: DefaultFilter[]
}

export interface UseAdvancedFiltersResult {
  /** Current filter rows in builder */
  filters: FilterRow[]

  /** Set filter rows */
  setFilters: (filters: FilterRow[]) => void

  /** Whether drawer is open */
  isDrawerOpen: boolean

  /** Open drawer */
  openDrawer: () => void

  /** Close drawer */
  closeDrawer: () => void

  /** Convert filter rows to API filter format */
  apiFilters: Filter[]

  /** Count of valid applied filters */
  filterCount: number

  /** Apply current filters */
  applyFilters: () => void

  /** Revert draft filters to last applied state (for Cancel) */
  revertFiltersToApplied: () => void

  /** Clear all filters */
  clearFilters: () => void

  /** Reset filters to metadata defaults */
  resetFiltersToDefaults: () => void

  /** Whether metadata defaults exist */
  hasDefaultFilters: boolean
}

/**
 * Convert metadata DefaultFilter to FilterRow format
 */
function convertDefaultToFilterRow(defaultFilter: DefaultFilter): FilterRow {
  return {
    id: crypto.randomUUID(),
    field: defaultFilter.key,
    operator: defaultFilter.constraint_type as ConstraintType,
    value: defaultFilter.value,
    values: defaultFilter.values,
    value2: defaultFilter.value2,
  }
}

/**
 * Hook to manage advanced filter state.
 *
 * @param options - Optional configuration including default filters
 * @returns Advanced filter state and actions
 */
export function useAdvancedFilters(
  options: UseAdvancedFiltersOptions = {}
): UseAdvancedFiltersResult {
  const { defaultFilters = [] } = options

  // Convert metadata defaults to FilterRow format
  const metadataDefaults = useMemo(
    () => defaultFilters.map(convertDefaultToFilterRow),
    [defaultFilters]
  )

  // Initialize with defaults if available
  const [filters, setFilters] = useState<FilterRow[]>(() =>
    metadataDefaults.length > 0 ? [...metadataDefaults] : []
  )
  const [appliedFilters, setAppliedFilters] = useState<FilterRow[]>(() =>
    metadataDefaults.length > 0 ? [...metadataDefaults] : []
  )
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Track if we've initialized from async-loaded defaults
  const hasInitializedFromDefaults = useRef(false)

  // Sync with metadata defaults when they become available (async loading)
  // This handles the case where metadata loads after initial render
  useEffect(() => {
    if (!hasInitializedFromDefaults.current && defaultFilters.length > 0) {
      hasInitializedFromDefaults.current = true
      const freshDefaults = defaultFilters.map(convertDefaultToFilterRow)
      setFilters(freshDefaults)
      setAppliedFilters(freshDefaults)
    }
  }, [defaultFilters])

  // Convert filter rows to API format
  const apiFilters = useMemo<Filter[]>(() => {
    return appliedFilters
      .filter((f) => {
        // Filter must have field and operator
        if (!f.field || !f.operator) return false

        // Check if operator requires value
        if (operatorRequiresValue(f.operator) && f.value === undefined) return false

        // Check if operator requires multiple values
        if (operatorRequiresMultipleValues(f.operator)) {
          if (!f.values || f.values.length === 0) return false
        }

        // Check if operator requires range
        if (operatorRequiresRange(f.operator)) {
          if (f.value === undefined || f.value2 === undefined) return false
        }

        return true
      })
      .flatMap((f) => {
        // Handle BETWEEN operator - convert to two filters (GE + LE)
        if (f.operator === 'BETWEEN') {
          return [
            {
              key: f.field,
              constraint_type: 'GE' as const,
              value: f.value,
            },
            {
              key: f.field,
              constraint_type: 'LE' as const,
              value: f.value2,
            },
          ]
        }

        const apiFilter: Filter = {
          key: f.field,
          constraint_type: f.operator,
        }

        // Add value based on operator type
        if (operatorRequiresMultipleValues(f.operator)) {
          apiFilter.values = f.values
        } else if (operatorRequiresValue(f.operator)) {
          apiFilter.value = f.value
        }

        return [apiFilter]
      })
  }, [appliedFilters])

  // Count valid filters
  const filterCount = apiFilters.length

  // Open drawer
  const openDrawer = () => {
    setIsDrawerOpen(true)
  }

  // Close drawer
  const closeDrawer = () => {
    setIsDrawerOpen(false)
  }

  // Apply filters
  const applyFilters = () => {
    setAppliedFilters([...filters])
  }

  // Revert draft filters to last applied state (for Cancel)
  const revertFiltersToApplied = useCallback(() => {
    setFilters([...appliedFilters])
  }, [appliedFilters])

  // Clear all filters (draft only - requires Apply to take effect)
  const clearFilters = () => {
    setFilters([])
    // NOTE: Do NOT update appliedFilters here - user must click Apply
  }

  // Reset filters to metadata defaults (draft only - requires Apply to take effect)
  const resetFiltersToDefaults = useCallback(() => {
    // Generate fresh IDs for each default filter
    const freshDefaults = defaultFilters.map(convertDefaultToFilterRow)
    setFilters(freshDefaults)
    // NOTE: Do NOT update appliedFilters here - user must click Apply
  }, [defaultFilters])

  // Whether metadata defaults exist
  const hasDefaultFilters = defaultFilters.length > 0

  return {
    filters,
    setFilters,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    apiFilters,
    filterCount,
    applyFilters,
    revertFiltersToApplied,
    clearFilters,
    resetFiltersToDefaults,
    hasDefaultFilters,
  }
}
